import { useState } from 'react';
import { reportApi } from '../api';
import { useAuth } from '../AuthContext';
import { exportCsv } from '../components/exportCsv';
import {
  IconChevronDown, IconClock, IconDiscount, IconDollarCircle, IconDownload, IconExternal,
  IconHandCash, IconMoneyUp, IconStamp, IconThumbUp,
} from '../components/icons';
import {
  Empty, ErrorNote, Spinner, TH_LOCALE, dateTh, dayLabel, money, shiftDate, th, todayIso, useLoader,
} from '../components/common';

// เงินเข้าบัญชี always shows all five channels.
const PAYMENT_METHODS = ['cash', 'qr_promptpay', 'credit_card', 'transfer', 'e_wallet', 'agency'];

const PRESETS = [
  { key: 'today', label: 'วันนี้', range: () => [todayIso(), todayIso()] },
  { key: 'yesterday', label: 'เมื่อวาน', range: () => [shiftDate(todayIso(), -1), shiftDate(todayIso(), -1)] },
  // These two stand for a family of ranges, so they carry a caret.
  { key: 'week', label: '7 วันที่แล้ว', menu: true, range: () => [shiftDate(todayIso(), -6), todayIso()] },
  { key: 'month', label: 'เดือนนี้', menu: true, range: () => [`${todayIso().slice(0, 7)}-01`, todayIso()] },
];

// One colour per service row, reused by the bullet and its proportion bar.
const POP_COLORS = ['#2f4a3c', '#88cf9a', '#d6a657', '#6ba8e5', '#b98a5e', '#5ec8c0'];

/** The toolbar shows a bare day — the weekday belongs to the booking screens. */
const dayTh = (iso) => dayLabel(iso, TH_LOCALE, { day: 'numeric', month: 'long', year: 'numeric' });

/** รายงาน — the dashboard. */
export default function Report() {
  const { branches } = useAuth();
  const [branchScope, setBranchScope] = useState('all');
  const [preset, setPreset] = useState('today');
  const [[from, to], setRange] = useState(PRESETS[0].range());
  // The toggle picks what the proportion column measures; the column headers
  // pick what the rows are ordered by. Two controls, two states.
  const [popMetric, setPopMetric] = useState('times');
  const [popSort, setPopSort] = useState('revenue');

  const pick = (p) => {
    setPreset(p.key);
    setRange(p.range());
  };

  const pickFrom = (value) => {
    if (!value) return;
    setPreset(null);
    setRange(value > to ? [value, value] : [value, to]);
  };

  const pickTo = (value) => {
    if (!value) return;
    setPreset(null);
    setRange(value < from ? [value, value] : [from, value]);
  };

  const report = useLoader(
    () => reportApi.dashboard(from, to, branchScope),
    [from, to, branchScope],
  );
  // The dashboard totals a range; this is the same range day by day, which is
  // what tells you whether a good week was steady or one busy Saturday.
  const daily = useLoader(
    () => reportApi.daily(from, to, branchScope),
    [from, to, branchScope],
  );

  if (report.loading && !report.data) return <Spinner />;
  if (report.error) return <ErrorNote error={report.error} />;

  const d = report.data;

  return (
    <>
      <div className="toolbar">
        <select
          className="chip"
          value={branchScope}
          onChange={(e) => setBranchScope(e.target.value)}
          aria-label="เลือกสาขารายงาน"
        >
          <option value="all">ทุกสาขา</option>
          {branches.map((b) => (
            <option key={b.id} value={b.id}>สาขา {b.code}</option>
          ))}
        </select>
        <span className="tool-sep" />
        {/* Two ends of a range, both editable — a preset just fills them in. */}
        <label className="date-box day-only">
          <span>{dayTh(from)}</span>
          <input
            type="date"
            value={from}
            aria-label="วันที่เริ่มต้น"
            onInput={(e) => pickFrom(e.currentTarget.value)}
          />
        </label>
        <label className="date-box day-only">
          <span>{dayTh(to)}</span>
          <input
            type="date"
            value={to}
            aria-label="วันที่สิ้นสุด"
            onInput={(e) => pickTo(e.currentTarget.value)}
          />
        </label>
        <span className="tool-sep" />
        {PRESETS.map((p) => (
          <button
            key={p.key}
            className={`chip preset${preset === p.key ? ' active' : ''}`}
            onClick={() => pick(p)}
          >
            <IconClock size={15} />
            {p.label}
            {p.menu && <IconChevronDown size={14} />}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <button
          className="btn-solid"
          onClick={() => exportCsv(`report-${from}_${to}`, [
            { label: 'รายการ', value: (r) => r.label },
            { label: 'จำนวน', value: (r) => r.value },
          ], [
            { label: 'รายรับทั้งหมด', value: d.revenue.total },
            { label: 'รายรับจากการใช้บริการ', value: d.revenue.serviceRevenue },
            { label: 'ยอดตัดจากแพ็กเกจ', value: d.revenue.packageRedeemed },
            { label: 'ยอดขายแพ็กเกจ', value: d.revenue.packageRevenue },
            { label: 'ยอดขายสินค้า', value: d.revenue.productRevenue },
            { label: 'รายรับแคชเชียร์', value: d.revenue.cashierIncome },
            { label: 'ทิป', value: d.revenue.tipTotal },
            { label: 'ชาร์จ', value: d.revenue.serviceCharge },
            { label: 'จำนวนบิล', value: d.bills.count },
            { label: 'จำนวนลูกค้า', value: d.bills.guestCount },
            { label: 'ราคาเฉลี่ยต่อบิล', value: d.bills.averagePerBill },
            { label: 'ราคาเฉลี่ยต่อคน', value: d.bills.averagePerGuest },
            { label: 'เงินเข้าบัญชี', value: d.payments.total },
            ...d.payments.byMethod.map((m) => ({ label: `  ${th(m.method)}`, value: m.amount })),
            { label: 'ค่ามือหมอนวด', value: d.cost.therapistCommission },
            { label: 'ส่วนลด', value: d.cost.discountTotal },
            { label: 'ค่าคอมเอเจนซี่', value: d.cost.agencyCommission },
            { label: 'รายจ่ายแคชเชียร์', value: d.cost.cashierExpense },
          ])}
        >
          <IconDownload size={16} /> ดาวน์โหลดข้อมูล
        </button>
      </div>

      {/* Three columns, then the costs stacked beside them — the coloured band
          is the card's header, not the whole card. */}
      <div className="report-top">
        <div className="rep-card rep-revenue">
          <div className="rep-head">
            <span>รายรับทั้งหมด <Help>อิงตาม วันและเวลาที่ลูกค้าเข้าใช้บริการ</Help></span>
            <b>{money(d.revenue.total)}</b>
          </div>
          <div className="rep-sub">อิงตาม วันและเวลาที่ลูกค้าเข้าใช้บริการ</div>
          <div className="rep-body">
            <Line label="รายรับจากการใช้บริการ" value={d.revenue.serviceRevenue} help />
            <Line label="ยอดตัดจากแพ็กเกจ" value={d.revenue.packageRedeemed} indent help />
            <Line label="ยอดขายแพ็กเกจ" value={d.revenue.packageRevenue} />
            <Line label="ยอดขายสินค้า" value={d.revenue.productRevenue} />
            <Line label="รายรับแคชเชียร์" value={d.revenue.cashierIncome} />
            <div className="rep-group">รายการอื่น ๆ</div>
            <Line label="ทิป" value={d.revenue.tipTotal} indent muted />
            <Line label="ชาร์จ" value={d.revenue.serviceCharge} indent muted />
          </div>
        </div>

        <div className="rep-card">
          <div className="rep-plain-head">จำนวนบิลบริการทั้งหมด</div>
          <div className="rep-big">{d.bills.count} ({d.bills.guestCount} คน)</div>
          <div className="rep-avg">
            <div>
              <div className="label">ราคาเฉลี่ยต่อบิล</div>
              <div className="rep-big sm">{money(d.bills.averagePerBill)}</div>
            </div>
            <div>
              <div className="label">ราคาเฉลี่ยต่อคน</div>
              <div className="rep-big sm">{money(d.bills.averagePerGuest)}</div>
            </div>
          </div>

          {/* Flush against the bills panel above it — one column, two bands. */}
          <div className="rep-card rep-payments">
            <div className="rep-head">
              <span>เงินเข้าบัญชี <Help>อิงตาม วันและเวลาที่ร้านระบุเวลาชำระเงินเข้ามา</Help></span>
              <b>{money(d.payments.total)}</b>
            </div>
            <div className="rep-sub">อิงตาม วันและเวลาที่ร้านระบุเวลาชำระเงินเข้ามา</div>
            <div className="rep-methods">
              <div className="rep-methods-head">ช่องทางการชำระเงิน</div>
              <div className="rep-method-grid">
                {PAYMENT_METHODS.map((m) => (
                  <div className="rep-method" key={m}>
                    {/* Card takings are settled by the acquirer, so their row
                        alone points off to the settlement report. */}
                    <span>{th(m)}{m === 'credit_card' && <IconExternal size={16} />}</span>
                    <b>{money((d.payments.byMethod.find((p) => p.method === m) || {}).amount || 0)}</b>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="rep-costs">
          <Cost label="ค่ามือหมอนวด" value={d.cost.therapistCommission} icon={IconHandCash} />
          <Cost label="ส่วนลด" value={d.cost.discountTotal} icon={IconDiscount} />
          <Cost label="ค่าคอมเอเจนซี่" value={d.cost.agencyCommission} icon={IconMoneyUp} />
          <Cost label="รายจ่ายแคชเชียร์" value={d.cost.cashierExpense} icon={IconStamp} />
        </div>
      </div>

      {d.branchBreakdown?.length > 1 && (
        <div className="card card-pad" style={{ marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 12px' }}>รายรับแยกตามสาขา</h3>
          <table className="data">
            <thead>
              <tr>
                <th>สาขา</th>
                <th className="num">บิล</th>
                <th className="num">ลูกค้า</th>
                <th className="num">บริการ</th>
                <th className="num">สินค้า</th>
                <th className="num">แพ็กเกจ</th>
                <th className="num">ค่ามือ</th>
                <th className="num">รายรับรวม</th>
              </tr>
            </thead>
            <tbody>
              {d.branchBreakdown.map((b) => (
                <tr key={b.branchId}>
                  <td><b>{b.code}</b> · {b.nameTh || b.nameEn}</td>
                  <td className="num">{b.billCount}</td>
                  <td className="num">{b.guestCount}</td>
                  <td className="num">{money(b.serviceRevenue)}</td>
                  <td className="num">{money(b.productRevenue)}</td>
                  <td className="num">{money(b.packageRevenue)}</td>
                  <td className="num">{money(b.therapistCommission)}</td>
                  <td className="num" style={{ fontWeight: 700 }}>{money(b.totalRevenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="report-bottom">
        <div className="card rep-panel">
          <div className="rep-panel-head">
            <h3>บริการยอดนิยม <Help /></h3>
            <div className="pop-toggle">
              <button
                className={popMetric === 'times' ? 'on' : ''}
                title="สัดส่วนจำนวนครั้ง"
                onClick={() => setPopMetric('times')}
              >
                <IconThumbUp size={18} />
              </button>
              <button
                className={popMetric === 'revenue' ? 'on' : ''}
                title="สัดส่วนรายได้"
                onClick={() => setPopMetric('revenue')}
              >
                <IconDollarCircle size={18} />
              </button>
            </div>
          </div>
          <div className="rep-panel-body">
            {!d.popularServices.length ? <Empty icon="report" />
              : <PopularServices rows={d.popularServices} metric={popMetric} sort={popSort} onSort={setPopSort} />}
          </div>
        </div>

        <div className="card rep-panel">
          <div className="rep-panel-head">
            <h3><IconClock size={20} className="head-icon" /> จำนวนลูกค้าตามเวลา</h3>
          </div>
          <div className="rep-panel-body">
            {!d.guestsByHour.length ? <Empty icon="report" /> : <GuestCurve rows={d.guestsByHour} />}
          </div>
        </div>
      </div>

      <div className="card card-pad" style={{ marginTop: 24 }}>
        <h3 style={{ margin: '0 0 12px' }}>สรุปรายวัน</h3>
        {daily.loading ? <Spinner />
          : daily.error ? <ErrorNote error={daily.error} />
          : !daily.data?.days?.length ? <Empty icon="report" message="ไม่มีข้อมูลในช่วงนี้" />
          : (
            <table className="data">
              <thead>
                <tr>
                  <th>วันที่</th>
                  <th className="num">บิล</th>
                  <th className="num">ลูกค้า</th>
                  <th className="num">รายได้บริการ</th>
                  <th className="num">รายได้สินค้า</th>
                  <th className="num">แพ็กเกจ</th>
                  <th className="num">ส่วนลด</th>
                  <th className="num">ค่ามือ</th>
                  <th className="num">รวม</th>
                </tr>
              </thead>
              <tbody>
                {daily.data.days.map((r) => {
                  const total = Number(r.service_revenue) + Number(r.product_revenue)
                    + Number(r.package_revenue);
                  return (
                    <tr key={r.business_date}>
                      <td>{dateTh(r.business_date)}</td>
                      <td className="num">{r.service_bill_count}</td>
                      <td className="num">{r.guest_count}</td>
                      <td className="num">{money(r.service_revenue)}</td>
                      <td className="num">{money(r.product_revenue)}</td>
                      <td className="num">{money(r.package_revenue)}</td>
                      <td className="num">{money(r.discount_total)}</td>
                      <td className="num">{money(r.therapist_commission)}</td>
                      <td className="num" style={{ fontWeight: 700 }}>{money(total)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="grand">
                  <td>รวม {daily.data.days.length} วัน</td>
                  <td className="num">{daily.data.days.reduce((s2, r) => s2 + r.service_bill_count, 0)}</td>
                  <td className="num">{daily.data.days.reduce((s2, r) => s2 + r.guest_count, 0)}</td>
                  <td className="num">{money(daily.data.days.reduce((s2, r) => s2 + Number(r.service_revenue), 0))}</td>
                  <td className="num">{money(daily.data.days.reduce((s2, r) => s2 + Number(r.product_revenue), 0))}</td>
                  <td className="num">{money(daily.data.days.reduce((s2, r) => s2 + Number(r.package_revenue), 0))}</td>
                  <td className="num">{money(daily.data.days.reduce((s2, r) => s2 + Number(r.discount_total), 0))}</td>
                  <td className="num">{money(daily.data.days.reduce((s2, r) => s2 + Number(r.therapist_commission), 0))}</td>
                  <td className="num">
                    {money(daily.data.days.reduce((s2, r) => s2 + Number(r.service_revenue)
                      + Number(r.product_revenue) + Number(r.package_revenue), 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
      </div>
    </>
  );
}

function Line({ label, value, indent, muted, help }) {
  return (
    <div className={`rep-line${indent ? ' indent' : ''}${muted ? ' muted' : ''}`}>
      <span>{label}{help && <Help>{label}</Help>}</span>
      <b>{money(value)}</b>
    </div>
  );
}

/** The (?) the original puts beside a figure whose basis needs explaining. */
function Help({ children }) {
  return <span className="rep-help" title={typeof children === 'string' ? children : ''}>?</span>;
}

function Cost({ label, value, icon: Icon }) {
  return (
    <div className="rep-cost">
      <Icon size={24} />
      <div>
        <div className="label">{label}</div>
        <div className="value">{money(value)}</div>
      </div>
    </div>
  );
}

/**
 * บริการยอดนิยม. The bar is each service's share of the range's total — not of
 * the busiest service — which is what makes the (%) beside it add up to 100.
 */
function PopularServices({ rows, metric, sort, onSort }) {
  const measure = (s) => (metric === 'revenue' ? Number(s.revenue) : s.times_sold);
  const show = (v) => (metric === 'revenue' ? money(v) : v);
  const total = rows.reduce((sum, s) => sum + measure(s), 0);
  // Baht need a wider number slot than a count of visits does.
  const metricCls = `pop-metric${metric === 'revenue' ? ' money' : ''}`;
  const ordered = [...rows].sort((a, b) => (sort === 'revenue'
    ? Number(b.revenue) - Number(a.revenue)
    : b.times_sold - a.times_sold));

  return (
    <table className="data pop-table">
      <thead>
        <tr>
          <th>บริการ</th>
          <th className={`num sortable${sort === 'times' ? ' on' : ''}`} onClick={() => onSort('times')}>
            {metric === 'revenue' ? 'สัดส่วนรายได้' : 'สัดส่วนจำนวนครั้ง'}
            <span className="pop-arrow">↑</span>
          </th>
          <th className={`num sortable${sort === 'revenue' ? ' on' : ''}`} onClick={() => onSort('revenue')}>
            รายได้<span className="pop-arrow">↑</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>ทั้งหมด</td>
          <td>
            <div className={metricCls}>
              <span />
              <b>{show(total)}</b>
              <span />
            </div>
          </td>
          <td className="num">{money(rows.reduce((sum, s) => sum + Number(s.revenue), 0))}</td>
        </tr>
        {ordered.map((s, i) => {
          const share = total ? measure(s) / total : 0;
          const colour = POP_COLORS[i % POP_COLORS.length];
          return (
            <tr key={s.service_id}>
              <td><span className="pop-dot" style={{ background: colour }} />{s.name_en} {s.name_th}</td>
              <td>
                <div className={metricCls}>
                  <span className="pop-bar">
                    <span style={{ width: `${share * 100}%`, background: colour }} />
                  </span>
                  <b>{show(measure(s))}</b>
                  <span className="pop-pct">({Math.round(share * 100)}%)</span>
                </div>
              </td>
              <td className="num">{money(s.revenue)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** Catmull-Rom through the points, as the cubics an SVG path can carry. */
const spline = (pts) => pts.map(([x, y], i) => {
  if (!i) return `M${x} ${y}`;
  const [x0, y0] = pts[i - 2] || pts[i - 1];
  const [x1, y1] = pts[i - 1];
  const [x3, y3] = pts[i + 1] || pts[i];
  return `C${x1 + (x - x0) / 6} ${y1 + (y - y0) / 6} ${x - (x3 - x1) / 6} ${y - (y3 - y1) / 6} ${x} ${y}`;
}).join(' ');

/** จำนวนลูกค้าตามเวลา — the day as a filled curve against a Pax axis. */
function GuestCurve({ rows }) {
  const W = 640; const H = 214; const L = 46; const R = 12; const T = 12; const B = 26;
  // PostgreSQL only returns hours that contain bookings. Fill the whole
  // operating-day domain so the chart always reaches the 23:00 closing time.
  const guestsByHour = new Map(rows.map((h) => [Number(h.hour), Number(h.guest_count) || 0]));
  const reportedHours = rows.map((h) => Number(h.hour)).filter(Number.isFinite);
  const firstHour = Math.min(10, ...reportedHours);
  const lastHour = Math.max(23, ...reportedHours);
  const chartRows = Array.from(
    { length: lastHour - firstHour + 1 },
    (_, index) => {
      const hour = firstHour + index;
      return { hour, guest_count: guestsByHour.get(hour) || 0 };
    }
  );

  const peak = Math.max(1, ...chartRows.map((h) => h.guest_count));
  // Axis steps stay on 1/2/5/10… so the labels read as counts of people.
  const step = [1, 2, 5, 10, 20, 50].find((s) => peak / s <= 4) || 100;
  const top = Math.ceil((peak + step / 2) / step) * step;
  const ticks = Array.from({ length: top / step }, (_, i) => (i + 1) * step);

  const x = (i) => (chartRows.length < 2 ? (L + W - R) / 2 : L + ((W - L - R) * i) / (chartRows.length - 1));
  const y = (v) => H - B - ((H - B - T) * v) / top;
  const pts = chartRows.map((h, i) => [x(i), y(h.guest_count)]);
  const curve = spline(pts);
  const every = Math.ceil(chartRows.length / 8);

  return (
    <svg className="guest-curve" viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }}>
      {ticks.map((v) => (
        <g key={v}>
          <line className="grid" x1={L} y1={y(v)} x2={W - R} y2={y(v)} />
          <line className="tick" x1={L - 5} y1={y(v)} x2={L} y2={y(v)} />
          <text className="axis" x={L - 9} y={y(v) + 4} textAnchor="end">{v} Pax</text>
        </g>
      ))}
      {chartRows.map((h, i) => (i % every && i !== chartRows.length - 1 ? null : (
        <g key={h.hour}>
          <line className="grid" x1={x(i)} y1={T} x2={x(i)} y2={H - B} />
          <text className="axis" x={x(i)} y={H - B + 16} textAnchor="middle">
            {String(h.hour).padStart(2, '0')}
          </text>
        </g>
      )))}
      <line className="axis-line" x1={L} y1={T} x2={L} y2={H - B} />
      <path className="curve-fill" d={`${curve} L${pts[pts.length - 1][0]} ${H - B} L${pts[0][0]} ${H - B} Z`} />
      <path className="curve-line" d={curve} />
    </svg>
  );
}
